import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { adminApi, getToken, setToken } from "../lib/adminApi";

const AdminAuthContext = createContext({
    status: "checking",
    admin: null,
    login: async () => {},
    logout: () => {},
    reload: async () => {},
});

/** Possible status values: "checking" | "unauthenticated" | "authenticated" | "must_change_password". */
export function AdminAuthProvider({ children }) {
    const [state, setState] = useState({
        status: getToken() ? "checking" : "unauthenticated",
        admin: null,
    });

    const refresh = useCallback(async () => {
        const token = getToken();
        if (!token) {
            setState({ status: "unauthenticated", admin: null });
            return;
        }
        try {
            const { data } = await adminApi.get("/admin/me");
            setState({
                status: data.must_change_password
                    ? "must_change_password"
                    : "authenticated",
                admin: data,
            });
        } catch {
            setToken("");
            setState({ status: "unauthenticated", admin: null });
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await adminApi.post("/admin/login", {
            email,
            password,
        });
        setToken(data.access_token);
        setState({
            status: data.must_change_password
                ? "must_change_password"
                : "authenticated",
            admin: {
                email: data.email,
                must_change_password: data.must_change_password,
            },
        });
        return data;
    }, []);

    const logout = useCallback(() => {
        setToken("");
        setState({ status: "unauthenticated", admin: null });
    }, []);

    return (
        <AdminAuthContext.Provider
            value={{ ...state, login, logout, reload: refresh }}
        >
            {children}
        </AdminAuthContext.Provider>
    );
}

export function useAdminAuth() {
    return useContext(AdminAuthContext);
}
